import { createReducer } from "@reduxjs/toolkit";
import {
  initSpreadsheet,
  updateSpreadsheetData,
  updateSpreadsheetOverwrites,
  addSpreadsheetPlot,
  removeSpreadsheetPlot,
  updatePlotVariables,
  updateSpreadsheetPlot,
  updatePlotSize,
  setPlotRequired,
  setPlotLocked,
} from "../actions";

/**
 * Initial state for the SpreadsheetReducer.
 */
const initialState = {
  spreadsheetData: {
    data: [], // Spreadsheet data (rows and columns)
    config: {}, // Configuration settings for the spreadsheet
    overwrites: [], // List of overwrites applied to the data
    queuedOverwrites: [], // List of pending overwrites
    plots: {}, // Dictionary of plots associated with the spreadsheet
    changeCount: 0, // Tracks the number of data changes
  },
};

/**
 * Reducer for managing spreadsheet-related state and actions.
 */
const SpreadsheetReducer = createReducer(initialState, (builder) => {
  /**
   * Initialize the spreadsheet data.
   * Merges the incoming data with the current state.
   */
  builder.addCase(initSpreadsheet, (state, action) => {
    state.spreadsheetData = {
      ...state.spreadsheetData,
      ...action.payload,
    };
  });

  /**
   * Update the main spreadsheet data and increment the change counter.
   */
  builder.addCase(updateSpreadsheetData, (state, action) => {
    state.spreadsheetData.data = action.payload;
    state.spreadsheetData.changeCount++;
  });

  /**
   * Update queued overwrites for the spreadsheet.
   */
  builder.addCase(updateSpreadsheetOverwrites, (state, action) => {
    state.spreadsheetData.queuedOverwrites = action.payload;
  });

  /**
   * Add a new plot to the spreadsheet.
   */
  builder.addCase(addSpreadsheetPlot, (state, action) => {
    const {
      key,
      type,
      sheet,
      sheetName,
      headerX,
      headerY,
      unitX,
      unitY,
      cellsUsed,
      spec,
      title,
      histogramBinWidth,
      histogramRangeMin,
      histogramRangeMax,
    } = action.payload;

    state.spreadsheetData.plots[key] = {
      id: key,
      type: type || "default_type", // Default type if not provided
      sheet: sheet || null, // Sheet associated with the plot
      sheetName: sheetName || null, // Sheet name associated with sheet
      title: title || "", // Default title is empty
      headerX: headerX || null, // X-axis header
      headerY: headerY || null, // Y-axis header
      unitX: unitX || "", // Unit for X-axis
      unitY: unitY || "", // Unit for Y-axis,
      histogramBinWidth: histogramBinWidth || 0,
      histogramRangeMin: histogramRangeMin || 0,
      histogramRangeMax: histogramRangeMax || 0,
      cellsUsed: cellsUsed || [], // Data cells used in the plot
      spec: spec || {}, // Specification for the plot
      size: { x: 400, y: 350 }, // Default size of the plot
      position: { x: 150, y: 150 }, // Default position of the plot
      required: false, // Plot is not required by default
      locked: false, // Plot is not locked by default
    };
  });

  /**
   * Remove a plot from the spreadsheet by its key.
   */
  builder.addCase(removeSpreadsheetPlot, (state, action) => {
    delete state.spreadsheetData.plots[action.payload];
  });

  /**
   * Update the headers of an existing plot.
   */
  builder.addCase(updatePlotVariables, (state, action) => {
    const {
      key,
      title,
      headerX,
      headerY,
      histogramBinWidth,
      histogramRangeMin,
      histogramRangeMax,
    } = action.payload;
    state.spreadsheetData.plots[key] = {
      ...state.spreadsheetData.plots[key],
      title,
      headerX,
      headerY,
      histogramBinWidth,
      histogramRangeMin,
      histogramRangeMax,
    };
  });

  /**
   * Update the specification, units, and data of an existing plot.
   */
  builder.addCase(updateSpreadsheetPlot, (state, action) => {
    const { key, spec, unitX, unitY, newData } = action.payload;
    const cellsUsed = state.spreadsheetData.plots[key].cellsUsed;
    cellsUsed.data = newData; // Update the data in cellsUsed

    state.spreadsheetData.plots[key] = {
      ...state.spreadsheetData.plots[key],
      spec,
      unitX,
      unitY,
      cellsUsed,
    };
  });

  /**
   * Update the size of a specific plot.
   */
  builder.addCase(updatePlotSize, (state, action) => {
    const { key, size } = action.payload;
    state.spreadsheetData.plots[key].size = size;
  });

  /**
   * Mark a specific plot as required.
   */
  builder.addCase(setPlotRequired, (state, action) => {
    state.spreadsheetData.plots[action.payload].required = true;
  });

  /**
   * Mark a specific plot as locked, preventing further modifications.
   */
  builder.addCase(setPlotLocked, (state, action) => {
    state.spreadsheetData.plots[action.payload].locked = true;
  });
});

export default SpreadsheetReducer;
